import { createRoute, lazyRouteComponent, redirect } from '@tanstack/react-router'
import type { AnyRoute } from '@tanstack/react-router'
import { i18n } from '../../shared/i18n/i18n'
import { AdminGate } from './AdminGate'
import sv from './locales/sv.json'
import en from './locales/en.json'

i18n.addResourceBundle('sv', 'administration', sv, true, true)
i18n.addResourceBundle('en', 'administration', en, true, true)

export function buildAdministrationRoutes<TParent extends AnyRoute>(parent: TParent) {
  // Pathless layout route. Its component is the AdminGate, which checks the
  // signed-in user's role before rendering the child page via <Outlet />.
  // Every admin page is registered under this layout so a non-admin who
  // navigates directly (e.g. by URL) hits the gate instead of the page.
  const gate = createRoute({
    getParentRoute: () => parent,
    id: 'administration-gate',
    component: AdminGate,
  })

  // Bare /administration → /administration/overview so legacy bookmarks land
  // somewhere. The redirect doesn't go through the gate (no page to render).
  const indexRedirect = createRoute({
    getParentRoute: () => parent,
    path: '/administration',
    beforeLoad: () => {
      throw redirect({ to: '/administration/overview' })
    },
  })

  return [
    indexRedirect,
    createRoute({
      getParentRoute: () => parent,
      path: '/support/users',
      beforeLoad: () => {
        throw redirect({ to: '/administration/users' })
      },
    }),
    gate.addChildren([
      createRoute({
        getParentRoute: () => gate,
        path: '/administration/overview',
        component: lazyRouteComponent(
          () => import('./AdministrationOverviewPage'),
          'AdministrationOverviewPage',
        ),
      }),
      createRoute({
        getParentRoute: () => gate,
        path: '/administration/teams',
        component: lazyRouteComponent(
          () => import('./AdminTeamsListPage'),
          'AdminTeamsListPage',
        ),
      }),
      createRoute({
        getParentRoute: () => gate,
        // Literal `/new` must be declared before `/$teamId` so the router
        // matches the create page rather than treating "new" as a teamId.
        path: '/administration/teams/new',
        component: lazyRouteComponent(
          () => import('./AdminTeamNewPage'),
          'AdminTeamNewPage',
        ),
      }),
      createRoute({
        getParentRoute: () => gate,
        path: '/administration/teams/$teamId',
        component: lazyRouteComponent(
          () => import('./AdminTeamDetailPage'),
          'AdminTeamDetailPage',
        ),
      }),
      createRoute({
        getParentRoute: () => gate,
        path: '/administration/users',
        component: lazyRouteComponent(
          () => import('./AdminUsersListPage'),
          'AdminUsersListPage',
        ),
      }),
      createRoute({
        getParentRoute: () => gate,
        path: '/administration/users/new',
        // This page used to take the address to pre-fill as ?email=, and old history
        // entries and bookmarks still carry it. The pre-fill now rides in history state, so
        // drop the query and replace the entry: left in place, every later page view and a
        // /login?next= after an expired session would copy the address into more logs.
        beforeLoad: ({ location }) => {
          if (Object.keys(location.search).length > 0) {
            throw redirect({ to: '/administration/users/new', search: {}, replace: true })
          }
        },
        component: lazyRouteComponent(
          () => import('./AdminUserNewPage'),
          'AdminUserNewPage',
        ),
      }),
      createRoute({
        getParentRoute: () => gate,
        path: '/administration/users/$userId',
        component: lazyRouteComponent(
          () => import('./AdminUserDetailPage'),
          'AdminUserDetailPage',
        ),
      }),
      createRoute({
        getParentRoute: () => gate,
        path: '/administration/estates',
        component: lazyRouteComponent(
          () => import('./AdminEstatesListPage'),
          'AdminEstatesListPage',
        ),
      }),
      createRoute({
        getParentRoute: () => gate,
        path: '/administration/estates/new',
        component: lazyRouteComponent(
          () => import('./AdminEstateNewPage'),
          'AdminEstateNewPage',
        ),
      }),
      createRoute({
        getParentRoute: () => gate,
        path: '/administration/estates/$estateId',
        component: lazyRouteComponent(
          () => import('./AdminEstateDetailPage'),
          'AdminEstateDetailPage',
        ),
      }),
      // District-config sub-pages — small read-only views for now; the
      // legacy edit dialogs land in a follow-up.
      createRoute({
        getParentRoute: () => gate,
        path: '/administration/general',
        component: lazyRouteComponent(
          () => import('./AdminGeneralPage'),
          'AdminGeneralPage',
        ),
      }),
      createRoute({
        getParentRoute: () => gate,
        // Legacy URL is lowercase `contactperson`; keep verbatim for bookmark parity.
        path: '/administration/contactperson',
        component: lazyRouteComponent(
          () => import('./AdminContactPersonPage'),
          'AdminContactPersonPage',
        ),
      }),
      createRoute({
        getParentRoute: () => gate,
        path: '/administration/settings',
        component: lazyRouteComponent(
          () => import('./AdminSettingsPage'),
          'AdminSettingsPage',
        ),
      }),
      createRoute({
        getParentRoute: () => gate,
        path: '/administration/news',
        component: lazyRouteComponent(
          () => import('./AdminNewsListPage'),
          'AdminNewsListPage',
        ),
      }),
      createRoute({
        getParentRoute: () => gate,
        path: '/administration/news/new',
        component: lazyRouteComponent(
          () => import('./AdminNewsNewPage'),
          'AdminNewsNewPage',
        ),
      }),
      createRoute({
        getParentRoute: () => gate,
        path: '/administration/news/$newsId',
        component: lazyRouteComponent(
          () => import('./AdminNewsDetailPage'),
          'AdminNewsDetailPage',
        ),
      }),
      createRoute({
        getParentRoute: () => gate,
        path: '/administration/groups',
        component: lazyRouteComponent(
          () => import('./AdminGroupsPage'),
          'AdminGroupsPage',
        ),
      }),
      createRoute({
        getParentRoute: () => gate,
        path: '/administration/exports',
        component: lazyRouteComponent(
          () => import('./AdminOfficialReportsPage'),
          'AdminOfficialReportsPage',
        ),
      }),
    ]),
  ]
}
